import React, {ReactNode} from 'react';
import {Box, Button, ButtonProps, Chip, Divider, Stack, Typography} from "@mui/material";
import {alpha, darken} from "@mui/material/styles";
import {useAccountStyle} from "@containers/accountStyle/hooks/useAccountStyle";
import {adminCardSurface} from "@containers/adminAccounts/ui/adminSurface";
import {STATE_COLORS} from "@containers/adminAccounts/ui/stateColors";

export type HeroTone = 'success' | 'neutral' | 'info' | 'tenant' | 'accent' | 'warning'

export interface IHeroStatus {
	label: string,
	tone: HeroTone,
}

export interface IHeroFact {
	label: string,
	value: ReactNode,
}

interface IProps {
	title: ReactNode,
	subtitle?: ReactNode,
	// The avatar content: initials, a photo or an icon; the frame is the hero's.
	leading?: ReactNode,
	statuses?: IHeroStatus[],
	facts?: IHeroFact[],
	actions?: ReactNode,
}

// Fixed colours: a status reads the same in every account colour.
const TONES: Record<Exclude<HeroTone, 'accent'>, string> = {
	success: STATE_COLORS.active,
	neutral: STATE_COLORS.inactive,
	info: STATE_COLORS.multi,
	tenant: STATE_COLORS.tenant,
	warning: STATE_COLORS.waiting,
}

/**
 * Header of the admin cards: who or what the card is, its state at a glance, the key facts in one row and
 * the actions on the right.
 */
const AdminHero = ({title, subtitle, leading, statuses, facts, actions}: IProps) => {

	const {tokens} = useAccountStyle()

	const toneColor = (tone: HeroTone) => tone === 'accent' ? tokens.accent : TONES[tone]

	return (
		<Box
			sx={(theme) => ({
				...adminCardSurface(theme),
				borderRadius: 2.5,
				px: {xs: 1.5, md: 2},
				py: 1.5,
				'& .MuiButton-root': {textTransform: 'none', fontWeight: 600, fontSize: 13.5, height: 32, borderRadius: 2},
			})}
		>
			<Stack
				direction={{xs: 'column', md: 'row'}}
				alignItems={{md: 'center'}}
				justifyContent={'space-between'}
				gap={2}
			>
				<Stack direction={'row'} alignItems={'center'} gap={1.5} sx={{minWidth: 0}}>
					{leading && <Box sx={{
						width: 40,
						height: 40,
						flexShrink: 0,
						borderRadius: 2,
						overflow: 'hidden',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						bgcolor: alpha(tokens.accent, 0.1),
						color: tokens.accent,
						fontSize: 15,
						fontWeight: 700,
						'& .MuiAvatar-root': {width: '100%', height: '100%', borderRadius: 0, bgcolor: 'transparent', color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit'},
					}}>
						{leading}
					</Box>}
					<Box sx={{minWidth: 0}}>
						<Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
							<Typography
								component={'h1'}
								sx={{fontSize: {xs: 16, md: 18}, fontWeight: 700, lineHeight: 1.3, overflowWrap: 'anywhere'}}
							>
								{title}
							</Typography>
							{statuses?.map((status) => {
								const color = toneColor(status.tone)
								return <Chip
									key={status.label}
									size={'small'}
									label={status.label}
									sx={{height: 20, fontSize: 11.5, fontWeight: 600, bgcolor: alpha(color, 0.12), color}}
								/>
							})}
						</Stack>
						{subtitle && <Typography sx={{fontSize: 13, lineHeight: 1.4, mt: 0.25}} color={'text.secondary'}>
							{subtitle}
						</Typography>}
					</Box>
				</Stack>
				{actions && <Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'} sx={{flexShrink: 0}}>
					{actions}
				</Stack>}
			</Stack>

			{!!facts?.length && <>
				<Divider sx={{my: 1.25}}/>
				<Stack direction={'row'} flexWrap={'wrap'} columnGap={3.5} rowGap={1}>
					{facts.map((fact) => (
						<Box key={fact.label} sx={{minWidth: 0}}>
							<Typography sx={{fontSize: 11, lineHeight: 1.3, letterSpacing: 0.2}} color={'text.secondary'}>
								{fact.label}
							</Typography>
							<Typography sx={{fontSize: 13.5, fontWeight: 500, lineHeight: 1.4, overflowWrap: 'anywhere'}}>
								{fact.value}
							</Typography>
						</Box>
					))}
				</Stack>
			</>}
		</Box>
	);
};

/** An action of the hero: the main one filled with the account colour, the others outlined. */
export const HeroButton = ({primary, sx, ...props}: ButtonProps & { primary?: boolean }) => {
	const {tokens} = useAccountStyle()
	return <Button
		variant={primary ? 'contained' : 'outlined'}
		disableElevation
		{...props}
		sx={[
			primary
				? {bgcolor: tokens.accent, color: '#fff', '&:hover': {bgcolor: darken(tokens.accent, 0.15)}}
				: {color: 'text.primary', borderColor: 'divider', '&:hover': {borderColor: 'text.secondary', bgcolor: 'action.hover'}},
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	/>
}

export default AdminHero;
